import { Component, OnDestroy, OnInit } from '@angular/core';
import { Validators, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { FormControlValidatorService } from '../../../shared/sharedServices/form-control-validator.service';
import { SelectItem } from 'primeng/api';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { authConstants } from '../../auth.constants';
import { MuToastService } from 'src/app/components/shared/sharedServices/mu-toast.service';
import { UserDataService } from '../../../shared/sharedServices/user-data.service';
import { ObservableManager } from 'src/app/components/shared/types/observableManager.type';
import { LayoutAuthService } from 'src/app/components/layout/pages/layout-auth/layout-auth.service';
import { Actions } from 'src/app/components/shared/types/actions.type';
import { AuthRegisterActions } from './auth-register.actions.enum';
import { MuException } from 'src/app/components/shared/types/exception.type';
import { SharedAuthValidator } from 'src/app/components/shared/sharedConstants/auth.validators';
import { MuCityService } from 'src/app/components/shared/sharedProtos/city/mu-city.service';
import { sharedAuthConstants } from 'src/app/components/shared/sharedConstants/auth.constants';
import { MuValidationsSmsService } from 'src/app/components/shared/sharedServices/mu-validations-sms.service';
@Component({
  selector: 'app-auth-register',
  templateUrl: './auth-register.component.html',
  styleUrls: ['./auth-register.component.sass'],
})
export class AuthRegisterComponent implements OnInit, OnDestroy {
  registerForm: UntypedFormGroup;
  countries: SelectItem[] = [];
  toastLife: number;
  showLoginLoading: boolean;
  private observableManager = new ObservableManager();
  flag = true;
  actionList: Actions<AuthRegisterActions, any> = new Actions<AuthRegisterActions, any>();
  actionListEnum = AuthRegisterActions;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private validator: FormControlValidatorService,
    private router: Router,
    private muToastService: MuToastService,
    private userDataService: UserDataService,
    private layoutAuthService: LayoutAuthService<any>,
    private muValidationsSmsService: MuValidationsSmsService,
    private muCityService: MuCityService
  ) {}

  async ngOnInit(): Promise<void> {
    this.buildForm();
    this.toastLife = environment.toastData.life;
    this.observableManager.watch(this.userDataService.getTokenLocal()).subscribe(token => {
      if (token && this.flag) {
        this.initData();
        this.flag = false;
      }
    });
    this.layoutAuthService.updateSettings({ tab: 1 });
    this.actionList.on(AuthRegisterActions.CHANGE_COUNTRY, () => {
      this.countryNumber();
    });
    this.actionList.on(AuthRegisterActions.SEND_SMS, () => {
      this.sendSms();
    });
  }

  ngOnDestroy(): void {
    this.observableManager.release();
  }

  /**
   * init data
   */
  async initData(): Promise<void> {
    localStorage.setItem(authConstants.codePhone, 'not');
    await this.getCountriesList();
  }

  /**
   * method to build a reactive form with form builder
   *
   * @memberof AuthRegisterComponent.buildForm
   */
  buildForm(): void {
    const sharedAuthValidator = new SharedAuthValidator(this.validator);
    this.registerForm = this.formBuilder.group({
      isInvalidForm: true,
      clientPhone: sharedAuthValidator.getPhone(),
      country: [null, Validators.required],
    });
  }

  /**
   * method to change validations for phone number
   *
   * @memberof AuthRegisterComponent.countryNumber
   */
  countryNumber(): void {
    if (this.registerForm?.get('country').value === authConstants.dropdownCountries.co.id) {
      this.changeValidators(authConstants.countryCode.cop);
    } else if (this.registerForm?.get('country').value === authConstants.dropdownCountries.mx.id) {
      this.changeValidators(authConstants.countryCode.mx);
    }
    this.registerForm.controls.clientPhone.updateValueAndValidity();
  }

  /**
   * method to validate number
   *
   * @return {*}  {Promise<void>}
   * @memberof AuthRegisterComponent.sendSms
   */
  async sendSms(): Promise<void> {
    if (this.registerForm.invalid) {
      return environment.guardClause;
    }
    try {
      this.showLoginLoading = true;
      const countryId: number = this.registerForm.get('country').value;
      const message = await this.muValidationsSmsService.profileSendSms(this.registerForm, 'clientPhone', countryId);
      localStorage.setItem(authConstants.storagePhone, JSON.stringify(this.registerForm.value));
      if (this.router.routerState?.snapshot.url.toString().includes(sharedAuthConstants.url.verifyCodeUp)) {
        localStorage.setItem(authConstants.codePhone, 'yes');
        console.log(message);
      }
      this.router.navigate(['crear-cuenta/verificacion']);
    } catch (e: any | MuException) {
      this.showLoginLoading = false;
      this.muValidationsSmsService.showToastErrorValidatePhone(this.muToastService, e.key, e);
    }
  }

  /**
   *
   *
   * @private
   * @param {number} length
   * @memberof AuthRegisterComponent.changeValidators
   */
  private changeValidators(length: number): void {
    this.registerForm.controls.clientPhone.setValidators([
      Validators.required,
      Validators.minLength(length),
      Validators.maxLength(length),
      this.validator.isValidPhone(),
    ]);
  }

  /**
   * method to get the list of countries
   *
   * @return {*}  {Promise<void>}
   * @memberof AuthRegisterComponent.getCountriesList
   */
  async getCountriesList(): Promise<void> {
    try {
      this.countries = [];
      const countries = await this.muCityService.getCountries();
      if (countries) {
        countries.forEach(country => {
          if (country.country_active) {
            const itemCountry = environment.countries.find(c => c.id === country.id);
            this.countries.push({
              label: itemCountry.indicative,
              value: itemCountry.id,
              icon: this.iconSelect(itemCountry.id),
            });
          }
        });
      }

      const tmpData = JSON.parse(localStorage.getItem(authConstants.storagePhone));
      if (this.countries.length > 0) {
        this.registerForm.get('country').setValue(tmpData?.country || this.countries[0].value, { emitEvent: false });
      }
    } catch (e: any | MuException) {
      this.muToastService.showSpecificToast({
        exception: e,
        key: 'toast-layout',
        severity: 'error',
        summary: 'context.register.toast.error.countriesList.title',
        detail: 'context.register.toast.error.countriesList.text',
        life: this.toastLife,
      });
    }
  }

  /**
   * method to set the country icon
   *
   * @private
   * @param {number} id
   * @return {*}  {string}
   * @memberof AuthRegisterComponent.iconSelect
   */
  private iconSelect(id: number): string {
    if (id === authConstants.dropdownCountries.co.id) {
      return authConstants.dropdownCountries.co.icon;
    } else if (id === authConstants.dropdownCountries.mx.id) {
      return authConstants.dropdownCountries.mx.icon;
    }
  }
}
